import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { InfiniteScrollConceptAutocomplete } from "../../../components/RegulationsTable/FilterBar/InfiniteScrollConceptAutocomplete";
import { fetchConceptsByCodes } from "../../../components/RegulationsTable/FilterBar/filterBarUtils";
import { aiPlanFilterFieldSx } from "../../../constants/regulationDetailsNavTabStyles";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import type { ConceptValue } from "../../../services/regulationsService";
import { CONCEPT_VALUE_ALL_OPTION } from "../constants";

const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const CONCEPT_PAGE_SIZE = 20;

const ALL_OPTION: ConceptValue = {
  id: CONCEPT_VALUE_ALL_OPTION.id,
  code: CONCEPT_VALUE_ALL_OPTION.code,
  name: CONCEPT_VALUE_ALL_OPTION.name,
};

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

export type ConceptValuesSelectionChange = {
  conceptValues: ConceptValue[];
  allValues: boolean;
};

type ConceptValuesMultiSelectProps = Readonly<{
  conceptCode: string;
  conceptName?: string;
  value: ConceptValue[];
  allValues?: boolean;
  onChange: (next: ConceptValuesSelectionChange) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Hide the "Concept Values *" label (e.g. embedded in query builder). */
  hideLabel?: boolean;
  /** When false, hide the exclusive "All" option. Defaults to true. */
  showAllOption?: boolean;
  placeholder?: string;
}>;

function isAllOption(option: ConceptValue): boolean {
  return option.code === CONCEPT_VALUE_ALL_OPTION.code;
}

function needsNameLookup(item: ConceptValue): boolean {
  const name = item.name?.trim() ?? "";
  return !name || name === item.code;
}

/**
 * Prefer known option names when selected chips were hydrated from codes only
 * (e.g. conditional-plan edit payload stores value as string[]).
 */
export function enrichConceptValuesWithNames(
  selected: ConceptValue[],
  lookups: ConceptValue[],
): ConceptValue[] {
  if (selected.length === 0 || lookups.length === 0) return selected;
  const byCode = new Map(lookups.map((item) => [item.code, item]));
  let changed = false;
  const next = selected.map((item) => {
    if (!needsNameLookup(item)) return item;
    const matched = byCode.get(item.code);
    const resolvedName = matched?.name?.trim() ?? "";
    if (!resolvedName || resolvedName === item.code) return item;
    changed = true;
    return {
      ...item,
      id: matched?.id || item.id,
      name: resolvedName,
      description: matched?.description ?? item.description,
    };
  });
  return changed ? next : selected;
}

function conceptValuesNamesEqual(
  left: ConceptValue[],
  right: ConceptValue[],
): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) =>
      item.code === right[index]?.code && item.name === right[index]?.name,
  );
}

function mergeOptionsWithSelected(
  options: ConceptValue[],
  selected: ConceptValue[],
): ConceptValue[] {
  const optionCodes = new Set(options.map((item) => item.code));
  const missingSelected = selected.filter((item) => !optionCodes.has(item.code));
  if (missingSelected.length === 0) return options;
  return [...missingSelected, ...options];
}

export function resolveConceptValuesSelectionChange(
  nextValue: ConceptValue[],
  previouslyAllValues: boolean,
): ConceptValuesSelectionChange {
  const withoutAll = nextValue.filter((item) => !isAllOption(item));
  const selectedAll = nextValue.some(isAllOption);

  if (previouslyAllValues) {
    // Selecting a specific value while All is on → auto-deselect All
    if (withoutAll.length > 0) {
      return { conceptValues: withoutAll, allValues: false };
    }
    if (!selectedAll) {
      return { conceptValues: [], allValues: false };
    }
    return { conceptValues: [], allValues: true };
  }

  // Selecting All while specific values are on → auto-deselect values
  if (selectedAll) {
    return { conceptValues: [], allValues: true };
  }

  return { conceptValues: withoutAll, allValues: false };
}

/** Map exclusivity result back to Autocomplete selection chips. */
export function normalizeConceptValuesSelection(
  nextValue: ConceptValue[],
  previousValue: ConceptValue[],
): ConceptValue[] {
  const previouslyAllValues = previousValue.some(isAllOption);
  const resolved = resolveConceptValuesSelectionChange(
    nextValue,
    previouslyAllValues,
  );
  return resolved.allValues ? [ALL_OPTION] : resolved.conceptValues;
}

export default function ConceptValuesMultiSelect({
  conceptCode,
  conceptName: _conceptName,
  value,
  allValues = false,
  onChange,
  disabled = false,
  error = false,
  helperText,
  hideLabel = false,
  showAllOption = true,
  placeholder = "Select Concept Values",
}: ConceptValuesMultiSelectProps) {
  const effectiveAllValues = showAllOption && allValues;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const requestedNameLookupCodesRef = useRef<Set<string>>(new Set());
  const [resolvedLookups, setResolvedLookups] = useState<ConceptValue[]>([]);

  // user-search concept_names = Dimension/Concept dropdown value as-is
  const conceptLookupKey = conceptCode.trim();

  const {
    input,
    setInput,
    options,
    loading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  } = usePaginatedConceptValues(conceptCode, CONCEPT_PAGE_SIZE);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((search?: string) => {
        void loadOptions(search);
      }, CONCEPT_SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  useEffect(() => {
    requestedNameLookupCodesRef.current = new Set();
    setResolvedLookups([]);
  }, [conceptLookupKey]);

  const handleInputChange = useCallback(
    (_event: unknown, nextValue: string) => {
      setInput(nextValue);
      if (!nextValue.trim()) {
        debouncedLoadOptions.cancel();
        void loadOptions(undefined);
        return;
      }
      debouncedLoadOptions(nextValue);
    },
    [debouncedLoadOptions, loadOptions, setInput],
  );

  const selectedRaw = useMemo(
    () =>
      effectiveAllValues
        ? [ALL_OPTION]
        : value.filter((item) => !isAllOption(item)),
    [effectiveAllValues, value],
  );

  const selectedForDisplay = useMemo(
    () =>
      enrichConceptValuesWithNames(selectedRaw, [
        ...options,
        ...resolvedLookups,
      ]),
    [options, resolvedLookups, selectedRaw],
  );

  // Resolve display names for values hydrated from API codes only.
  useEffect(() => {
    if (!conceptLookupKey || effectiveAllValues) return;

    const codesNeedingLookup = selectedRaw
      .filter((item) => {
        if (!needsNameLookup(item)) return false;
        const fromOptions = options.find((opt) => opt.code === item.code);
        if (fromOptions?.name?.trim() && fromOptions.name !== item.code) {
          return false;
        }
        return !requestedNameLookupCodesRef.current.has(item.code);
      })
      .map((item) => item.code)
      .filter(Boolean);

    if (codesNeedingLookup.length === 0) return;

    for (const code of codesNeedingLookup) {
      requestedNameLookupCodesRef.current.add(code);
    }

    let cancelled = false;
    void fetchConceptsByCodes(conceptLookupKey, codesNeedingLookup).then(
      (items) => {
        if (cancelled || items.length === 0) return;
        setResolvedLookups((prev) => {
          const byCode = new Map(prev.map((item) => [item.code, item]));
          for (const item of items) {
            byCode.set(item.code, item);
          }
          return Array.from(byCode.values());
        });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [conceptLookupKey, effectiveAllValues, options, selectedRaw]);

  // Persist resolved names into parent state so chips stay named after remount.
  useEffect(() => {
    if (effectiveAllValues) return;
    if (conceptValuesNamesEqual(selectedRaw, selectedForDisplay)) return;
    const improved = selectedForDisplay.some((item, index) => {
      const raw = selectedRaw[index];
      return Boolean(raw && needsNameLookup(raw) && !needsNameLookup(item));
    });
    if (!improved) return;
    onChangeRef.current({
      conceptValues: selectedForDisplay,
      allValues: false,
    });
  }, [effectiveAllValues, selectedForDisplay, selectedRaw]);

  const displayOptions = useMemo(() => {
    const merged = mergeOptionsWithSelected(options, selectedForDisplay).filter(
      (item) => !isAllOption(item),
    );
    return showAllOption ? [ALL_OPTION, ...merged] : merged;
  }, [options, selectedForDisplay, showAllOption]);

  return (
    <Box>
      {hideLabel ? null : (
        <Typography sx={formFieldLabelSx}>
          Concept Values
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
      )}
      <InfiniteScrollConceptAutocomplete
        label=""
        value={selectedForDisplay}
        onChange={(_event, nextValue) => {
          if (!showAllOption) {
            onChange({
              conceptValues: nextValue.filter((item) => !isAllOption(item)),
              allValues: false,
            });
            return;
          }
          onChange(resolveConceptValuesSelectionChange(nextValue, effectiveAllValues));
        }}
        options={displayOptions}
        inputValue={input}
        onInputChange={handleInputChange}
        loading={loading}
        hideSelectedWhenSearching
        selectedVisibilityMode="matchWhenSearchingAllWhenEmpty"
        hasMoreNext={hasMoreNext}
        loadingMore={loadingMore}
        onLoadMoreNext={loadNext}
        onLoadMorePrev={loadPrev}
        unlimitedSelection
        normalizeSelection={
          showAllOption ? normalizeConceptValuesSelection : undefined
        }
        hideSelectionCount
        selectedDisplayMode="plainText"
        placeholder={placeholder}
        disabled={disabled || !conceptCode}
        fullWidth
        error={error}
        helperText={helperText}
        sx={aiPlanFilterFieldSx}
      />
    </Box>
  );
}
